import { useContext, useState } from "react";
import { Box, Button, CircularProgress, Paper, TableContainer, Typography } from "@mui/material";
import { CheckCircleOutline, Close, Send } from "@mui/icons-material";
import { useOktaAuth } from "@okta/okta-react";
import { ModuleContext } from "../../context/ModuleContext";

const WSU_RED = "#A60F2D";

export const Reinvite = ({ userId }) => {
  const [state, setState] = useState({ waiting: false, result: null, error: null });

  const { setModule } = useContext(ModuleContext);
  const { authState } = useOktaAuth();

  const sendReinvite = async () => {
    setState((prev) => ({ ...prev, waiting: true }));

    try {
      const response = await fetch(
        `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/reinvite`,
        {
          method: "POST",
          signal: AbortSignal.timeout(360000),
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: authState.accessToken.accessToken,
          },
          body: JSON.stringify({ userId, callerId: authState.accessToken.claims.sub }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        const errorObj = JSON.parse(text);
        throw new Error(errorObj?.error?.message || "Failed to reinvite user.");
      }

      setState((prev) => ({ ...prev, waiting: false, result: "Reinvite email sent", error: null }));
    } catch (err) {
      setState((prev) => ({ ...prev, waiting: false, error: err.message }));
    }
  };

  if (state.waiting) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
      </Box>
    );
  }

  if (state.error) {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Reinvite failed
          </Typography>
          <Typography variant="body2">{state.error}</Typography>
        </Box>
      </TableContainer>
    );
  }

  if (state.result) {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ backgroundColor: "#2e7d32", color: "white", px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <CheckCircleOutline fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>Reinvite Sent</Typography>
        </Box>
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Confirmation
          </Typography>
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" color="text.secondary">{state.result}</Typography>
        </Box>
        <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            endIcon={<Close />}
            onClick={() => setModule((prev) => ({ ...prev, isVisible: false }))}
            sx={{ borderColor: "#ccc", color: "#666", "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" } }}
          >
            Close
          </Button>
        </Box>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
          Send Reinvite
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>{userId}</Typography>
      </Box>
      <Box sx={{ px: 3, py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Send a reinvite email to {userId}?
        </Typography>
      </Box>
      <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          endIcon={<Close />}
          onClick={() => setModule((prev) => ({ ...prev, isVisible: false }))}
          sx={{ borderColor: "#ccc", color: "#666", "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          endIcon={<Send />}
          onClick={sendReinvite}
          sx={{
            backgroundColor: WSU_RED,
            color: "white",
            px: 4,
            py: 1.25,
            fontWeight: 600,
            letterSpacing: 0.5,
            "&:hover": { backgroundColor: "#8a0c25" },
          }}
        >
          Send Reinvite
        </Button>
      </Box>
    </TableContainer>
  );
};
